import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    FaTrashAlt, FaUpload, FaSpinner, FaCheckSquare, FaSquare, FaTimes, FaFilter,
    FaSortAmountDown, FaImage, FaEllipsisV, FaAngleLeft, FaAngleRight, FaCheck,
    FaCogs, FaCoins, FaClock, FaAd, FaStopwatch
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Calling from '../../../api/Calling';
import generateCaptcha from '../../../components/captcha';
import { imageType, MAX_FILE_SIZE_IMAGE } from '../../../components/imageType';
import Loader from '../../../components/Loader';
import Navbar from '../../Quiz/Navbar';


// Component for file preview
const FilePreview = ({ file, onRemove }) => (
    <div className="flex items-center justify-between p-2 rounded-lg bg-[#F2F0FF] border border-main-border mb-2 group hover:bg-[#F5F8FF] transition">
        <div className="flex items-center truncate">
            <div className="w-8 h-8 bg-[#E9E5FF] rounded-full flex items-center justify-center mr-2">
                <FaImage className="text-main-blue" />
            </div>
            <div className="flex flex-col">
                <span className="text-xs font-medium truncate text-main-font" title={file.name}>
                    {file.name.length > 20 ? file.name.substring(0, 20) + '...' : file.name}
                </span>
                <span className="text-xs text-main-blue">
                    {(file.size / 1024).toFixed(1)} KB
                </span>
            </div>
        </div>
        <button
            onClick={onRemove}
            className="text-main-subfont hover:text-main-delete opacity-0 group-hover:opacity-100 transition-opacity"
        >
            <FaTimes size={14} />
        </button>
    </div>
);

// Component for image card
const ImageCard = ({ item, isMultiSelectMode, isSelected, onToggleSelect, onDelete }) => (
    <div className={`rounded-xl overflow-hidden bg-main-white shadow hover:shadow-md transition group
    ${isMultiSelectMode && isSelected ? 'ring-2 ring-main-blue' : ''}`}
    >
        <div className="relative pt-[100%] overflow-hidden">
            {isMultiSelectMode && (
                <div
                    className="absolute top-2 left-2 z-10 cursor-pointer"
                    onClick={onToggleSelect}
                >
                    {isSelected ? (
                        <div className="w-6 h-6 bg-main-blue rounded-full flex items-center justify-center">
                            <FaCheck size={12} className="text-white" />
                        </div>
                    ) : (
                        <div className="w-6 h-6 bg-white bg-opacity-75 rounded-full flex items-center justify-center border-2 border-main-border">
                        </div>
                    )}
                </div>
            )}
            <img
                src={item.image}
                alt={`Option ${item.option}`}
                className={`absolute top-0 left-0 w-full h-full object-cover transition duration-300 group-hover:scale-105 ${isMultiSelectMode ? 'cursor-pointer' : ''}`}
                onClick={() => isMultiSelectMode && onToggleSelect()}
            />
            {!isMultiSelectMode && (
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                        onClick={onDelete}
                        className="p-2 bg-main-delete text-white rounded-full transform transition hover:scale-110"
                    >
                        <FaTrashAlt size={14} />
                    </button>
                </div>
            )}
        </div>
        <div className="p-2 flex items-center justify-between bg-gradient-to-r from-[#F2F0FF] to-white">
            <div>
                <span className="font-semibold text-main-font text-sm">Option {item.option}</span>
                <p className="text-xs text-main-subfont">
                    {new Date(item.createdAt).toLocaleDateString()}
                </p>
            </div>
        </div>
    </div>
);

// Stats component
const StatsCard = ({ totalRecords, selectedOption, totalPages }) => (
    <div className="bg-gradient-to-br from-main-blue to-[#4F37C8] text-white rounded-xl p-4 mb-5 shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mt-10 -mr-10"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-main-yellow opacity-20 rounded-full -mb-8 -ml-8"></div>
        <div className="flex justify-between items-center relative z-10">
            <div>
                <h3 className="text-xl font-bold">Option {selectedOption}</h3>
                <div className="flex items-center mt-1">
                    <span className="text-3xl font-bold mr-2">{totalRecords}</span>
                    <span className="text-[#E9E5FF]">Images</span>
                </div>
            </div>
            <div className="text-right">
                <div className="text-xs text-[#E9E5FF]">Current Page</div>
                <div className="text-xl font-semibold flex items-center">
                    <span className="bg-white bg-opacity-20 px-2 py-1 rounded">1</span>
                    <span className="mx-1 text-sm">/</span>
                    <span>{totalPages}</span>
                </div>
            </div>
        </div>
    </div>
);

// Upload progress component
const UploadProgress = ({ progress }) => (
    <div className="mb-4">
        <div className="w-full bg-main-bg rounded-full h-3 overflow-hidden">
            <div
                className="bg-gradient-to-r from-main-blue to-[#4F37C8] h-3 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
            >
                <div className="w-full h-full bg-white bg-opacity-20 animate-pulse"></div>
            </div>
        </div>
        <div className="flex justify-between text-xs text-main-subfont mt-1">
            <span>Uploading...</span>
            <span className="font-medium">{progress}%</span>
        </div>
    </div>
);

// Custom Navbar with Images and Controls tabs
const Navbarin = ({ activeTab, onTabChange, isMultiSelectMode, selectedItems, deleteWhois }) => {
    return (
        <div className="flex border-b border-main-border overflow-x-auto">
            <button
                onClick={() => onTabChange('images')}
                className={`px-4 py-3 text-center min-w-[100px] transition-colors ${activeTab === 'images'
                    ? 'border-b-2 border-main-blue text-main-blue font-medium'
                    : 'text-main-subfont hover:text-main-font'
                    }`}
            >
                <span className="flex items-center justify-center">
                    <FaImage className="mr-2" /> Images
                </span>
            </button>
            <button
                onClick={() => onTabChange('controls')}
                className={`px-4 py-3 text-center min-w-[100px] transition-colors ${activeTab === 'controls'
                    ? 'border-b-2 border-main-blue text-main-blue font-medium'
                    : 'text-main-subfont hover:text-main-font'
                    }`}
            >
                <span className="flex items-center justify-center">
                    <FaCogs className="mr-2" /> Controls
                </span>
            </button>
            {
                activeTab === 'images' && isMultiSelectMode && selectedItems.length > 0 && (
                    <button
                        onClick={() => deleteWhois(selectedItems)}
                        className="px-3 my-2 py-1.5 rounded-full text-sm bg-main-delete text-white hover:bg-red-600 flex items-center transition-colors shadow-sm"
                    >
                        <FaTrashAlt className="mr-1.5" size={12} />
                        Delete ({selectedItems.length})
                    </button>
                )
            }
        </div>
    );
};

export default function Whois() {
    // Common state
    const [activeTab, setActiveTab] = useState('images');

    // Images tab state
    const [whoisOptions, setWhoisOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState('A');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [pageInfo, setPageInfo] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRecords: 0
    });
    const [selectedItems, setSelectedItems] = useState([]);
    const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
    const [dropActive, setDropActive] = useState(false);

    // Controls tab state
    const [controlsLoading, setControlsLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [controlsData, setControlsData] = useState({
        ad_position: 10,
        coin: 2,
        limit: 100,
        timeout_timing: 60,
        timing: 3
    });
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const MAX_FILE_COUNT = 100;
    const OPTIONS = ['A', 'B', 'C', 'D'];

    // Memoized value for checking if all items are selected
    const allSelected = useMemo(() =>
        whoisOptions.length > 0 && selectedItems.length === whoisOptions.length,
        [whoisOptions.length, selectedItems.length]);

    // Load data for active tab
    useEffect(() => {
        if (activeTab === 'images') {
            fetchImageData(selectedOption, 1);
        } else if (activeTab === 'controls') {
            fetchControlsData();
        }
    }, [activeTab]);

    // Fetch whois options (images)
    const fetchImageData = useCallback(async (option = selectedOption, page = 1) => {
        setIsLoading(true);
        try {
            const resp = await Calling?.getWhois(option, page, 100);

            if (resp.status) {
                setWhoisOptions(resp.data);
                setPageInfo({
                    currentPage: page,
                    totalPages: resp.totalPages,
                    totalRecords: resp.totalRecords
                });
                // Clear selected items when changing filters
                setSelectedItems([]);
            } else {
                toast.error(resp.message || 'Failed to load options');
            }
        } catch (error) {
            console.error('Get Whois Options Error:', error);
            toast.error('Failed to load options');
        } finally {
            setIsLoading(false);
        }
    }, [selectedOption]);

    // Fetch controls data
    const fetchControlsData = async () => {
        setControlsLoading(true);
        try {
            const resp = await Calling.getWhoisControle();
            if (resp?.status) {
                setControlsData(resp.data);
                setError(null);
            } else {
                setError(resp?.message || 'Failed to fetch controls data');
            }
        } catch (error) {
            console.error(error);
            setError('Failed to fetch controls data. Please try again.');
        } finally {
            setControlsLoading(false);
        }
    };

    // Insert new whois options (multiple files)
    const insertWhoisMultiple = async () => {
        if (selectedFiles.length === 0) {
            toast.error('Please select at least one image file');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            let successCount = 0;
            let failCount = 0;

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i];
                setUploadProgress(Math.floor((i / selectedFiles.length) * 100));

                const formData = new FormData();
                formData.append('option', selectedOption);
                formData.append('image', file);

                try {
                    const resp = await Calling.insertWhois(formData);
                    resp.status ? successCount++ : failCount++;
                } catch (error) {
                    failCount++;
                    console.error(`Failed to upload ${file.name}:`, error);
                }
            }

            setUploadProgress(100);

            if (successCount > 0) {
                toast.success(`Successfully uploaded ${successCount} image${successCount !== 1 ? 's' : ''}`);
            }

            if (failCount > 0) {
                toast.error(`Failed to upload ${failCount} image${failCount !== 1 ? 's' : ''}`);
            }

            setSelectedFiles([]);
            fetchImageData(selectedOption);

        } catch (error) {
            console.error('Insert Multiple Whois Error:', error);
            toast.error('Failed to complete the upload process');
        } finally {
            setTimeout(() => {
                setIsUploading(false);
                setUploadProgress(0);
            }, 500); // Short delay for better UX
        }
    };

    // Delete whois option(s)
    const deleteWhois = async (ids) => {
        if (!ids.length) return;

        const content = `You are about to delete reading reward data. To confirm this action, please solve the math problem below.`;
        const check = generateCaptcha(content);

        if (!check) {
            return toast.error('Answer is not correct');
        }

        setIsLoading(true);
        try {
            const body = { ids };
            const resp = await Calling?.deleteWhois(body);

            if (resp.status) {
                toast.success(ids.length > 1 ? `${ids.length} items deleted successfully!` : 'Item deleted successfully!');
                setSelectedItems([]);
                // Refresh the list after deletion
                fetchImageData(selectedOption);
            } else {
                toast.error(resp.message || 'Failed to delete selected items');
            }
        } catch (error) {
            console.error('Delete Whois Error:', error);
            toast.error('Failed to delete selected items');
        } finally {
            setIsLoading(false);
        }
    };

    // Handle file selection
    const handleFileChange = (e) => {
        const fileList = Array.from(e.target.files);

        if (fileList.length === 0) return;

        // Check file count limit
        if (fileList.length > MAX_FILE_COUNT) {
            toast.error(`You can only upload up to ${MAX_FILE_COUNT} files at once`);
            return;
        }

        // Filter and validate files
        const validFiles = [];
        const invalidTypeFiles = [];
        const oversizedFiles = [];

        fileList.forEach(file => {
            if (!imageType.includes(file.type)) {
                invalidTypeFiles.push(file.name);
            } else if (file.size > MAX_FILE_SIZE_IMAGE) {
                oversizedFiles.push(file.name);
            } else {
                validFiles.push(file);
            }
        });

        // Show error messages if needed
        if (invalidTypeFiles.length > 0) {
            const fileNames = invalidTypeFiles.length <= 3
                ? invalidTypeFiles.join(', ')
                : `${invalidTypeFiles.slice(0, 3).join(', ')} and ${invalidTypeFiles.length - 3} more`;
            toast.error(`Invalid file type: ${fileNames}. Only JPG, JPEG, and PNG are allowed.`);
        }

        if (oversizedFiles.length > 0) {
            const fileNames = oversizedFiles.length <= 3
                ? oversizedFiles.join(', ')
                : `${oversizedFiles.slice(0, 3).join(', ')} and ${oversizedFiles.length - 3} more`;
            toast.error(`File size exceeded: ${fileNames}. Maximum file size is 1MB.`);
        }

        // Set valid files
        setSelectedFiles(validFiles);
    };

    // Handle drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        setDropActive(true);
    };

    const handleDragLeave = () => {
        setDropActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDropActive(false);

        const fileList = Array.from(e.dataTransfer.files);
        if (fileList.length === 0) return;

        // Apply the same validation logic as handleFileChange
        if (fileList.length > MAX_FILE_COUNT) {
            toast.error(`You can only upload up to ${MAX_FILE_COUNT} files at once`);
            return;
        }

        const validFiles = [];
        const invalidTypeFiles = [];
        const oversizedFiles = [];

        fileList.forEach(file => {
            if (!imageType.includes(file.type)) {
                invalidTypeFiles.push(file.name);
            } else if (file.size > MAX_FILE_SIZE_IMAGE) {
                oversizedFiles.push(file.name);
            } else {
                validFiles.push(file);
            }
        });

        // Show error messages if needed
        if (invalidTypeFiles.length > 0) {
            const fileNames = invalidTypeFiles.length <= 3
                ? invalidTypeFiles.join(', ')
                : `${invalidTypeFiles.slice(0, 3).join(', ')} and ${invalidTypeFiles.length - 3} more`;
            toast.error(`Invalid file type: ${fileNames}. Only JPG, JPEG, and PNG are allowed.`);
        }

        if (oversizedFiles.length > 0) {
            const fileNames = oversizedFiles.length <= 3
                ? oversizedFiles.join(', ')
                : `${oversizedFiles.slice(0, 3).join(', ')} and ${oversizedFiles.length - 3} more`;
            toast.error(`File size exceeded: ${fileNames}. Maximum file size is 1MB.`);
        }

        // Set valid files
        setSelectedFiles(validFiles);
    };

    // Remove a file from selected files
    const removeSelectedFile = (index) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    // Toggle item selection
    const toggleItemSelection = (id) => {
        setSelectedItems(prev => {
            if (prev.includes(id)) {
                return prev.filter(item => item !== id);
            } else {
                return [...prev, id];
            }
        });
    };

    // Toggle select all
    const toggleSelectAll = () => {
        setSelectedItems(allSelected ? [] : whoisOptions.map(item => item._id));
    };

    // Toggle multi-select mode
    const toggleMultiSelectMode = () => {
        setIsMultiSelectMode(!isMultiSelectMode);
        setSelectedItems([]);
    };

    // Handle controls form changes
    const handleControlsChange = (e) => {
        const { name, value } = e.target;
        // Convert string values to numbers
        setControlsData({
            ...controlsData,
            [name]: parseInt(value, 10) || 0
        });
    };

    // Save controls
    const saveControls = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);

        // Basic validation
        if (controlsData.coin <= 0) {
            setSaving(false);
            return toast.error("Coins must be greater than zero.");
        }

        if (controlsData.limit <= 0) {
            setSaving(false);
            return toast.error("Limit must be greater than zero.");
        }

        if (controlsData.timing <= 0) {
            setSaving(false);
            return toast.error("Timing must be greater than zero.");
        }

        try {
            const body = {
                ad_position: controlsData.ad_position,
                coin: controlsData.coin,
                limit: controlsData.limit,
                timeout_timing: controlsData.timeout_timing,
                timing: controlsData.timing
            };

            const resp = await Calling.putWhoisControle(body);

            if (resp?.status) {
                setSuccess("Settings updated successfully!");
                toast.success("Settings updated successfully!");
                setTimeout(() => setSuccess(null), 3000);
            } else {
                setError(resp?.message || "Failed to update settings.");
                toast.error(resp?.message || "Failed to update settings.");
            }
        } catch (error) {
            console.error(error);
            setError("Failed to update settings. Please try again.");
            toast.error("Failed to update settings. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    // Load options when component mounts or option changes
    useEffect(() => {
        if (activeTab === 'images') {
            fetchImageData(selectedOption, 1);
        }
    }, [selectedOption, fetchImageData, activeTab]);

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Who is Beautiful</h1>
            <div className='bg-main-white rounded-t-xl shadow-lg'>
                <Navbar selectState={'whois'} />
                <hr className='border-main-border' />
            </div>

            <div className='bg-main-white shadow-lg overflow-hidden'>
                <Navbarin
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    isMultiSelectMode={isMultiSelectMode}
                    selectedItems={selectedItems}
                    deleteWhois={deleteWhois}
                />
                <hr className='border-main-border' />

                {/* Images Tab */}
                {activeTab === 'images' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-5">
                        {/* Left column: Upload form */}
                        <div className="lg:col-span-1">
                            <div className="p-4 rounded-xl bg-gradient-to-br from-main-bg to-[#F2F0FF] border border-main-border shadow-sm">
                                <h2 className="text-lg font-semibold mb-4 text-main-font flex items-center">
                                    <FaUpload className="mr-2 text-main-blue" /> Upload Images
                                </h2>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2 text-main-font">Choose Option:</label>
                                    <div className="flex space-x-2">
                                        {OPTIONS.map(option => (
                                            <button
                                                key={option}
                                                onClick={() => setSelectedOption(option)}
                                                className={`px-4 py-2 rounded-full transition-colors w-12 h-12 flex items-center justify-center font-bold ${selectedOption === option
                                                    ? 'bg-main-blue text-white shadow-md'
                                                    : 'bg-white hover:bg-[#F2F0FF] text-main-font border border-main-border'
                                                    }`}
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-2 text-main-font">
                                        Upload Images (JPG, JPEG, PNG - 1MB max):
                                    </label>
                                    <div
                                        className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${dropActive
                                            ? 'border-main-blue bg-[#F2F0FF]'
                                            : 'border-main-border bg-white hover:bg-[#F2F0FF] hover:border-main-blue'
                                            }`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                    >
                                        <label className="flex flex-col items-center justify-center w-full h-full">
                                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                <div className="w-12 h-12 mb-2 bg-[#F2F0FF] rounded-full flex items-center justify-center">
                                                    <FaUpload size={20} className="text-main-blue" />
                                                </div>
                                                <p className="mb-1 text-sm text-main-font">
                                                    <span className="font-semibold">Click to upload</span> or drag and drop
                                                </p>
                                                <p className="text-xs text-main-subfont">Up to {MAX_FILE_COUNT} files (1MB each)</p>
                                                {selectedFiles.length > 0 && (
                                                    <div className="mt-2 px-3 py-1 rounded-full bg-[#F2F0FF] text-main-blue text-xs font-medium">
                                                        {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected
                                                    </div>
                                                )}
                                            </div>
                                            <input
                                                type="file"
                                                className="hidden"
                                                accept=".jpg,.jpeg,.png"
                                                onChange={handleFileChange}
                                                multiple
                                            />
                                        </label>
                                    </div>
                                </div>

                                {/* Display selected files */}
                                {selectedFiles.length > 0 && (
                                    <div className="mb-4 max-h-48 overflow-y-auto p-2 rounded-lg bg-white border border-main-border">
                                        <div className="flex items-center justify-between mb-2">
                                            <h3 className="text-sm font-medium text-main-font">Selected Files</h3>
                                            <span className="text-xs text-main-blue">{selectedFiles.length} items</span>
                                        </div>
                                        <div className="space-y-1">
                                            {selectedFiles.map((file, index) => (
                                                <FilePreview
                                                    key={index}
                                                    file={file}
                                                    onRemove={() => removeSelectedFile(index)}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Upload progress bar */}
                                {isUploading && <UploadProgress progress={uploadProgress} />}

                                <button
                                    onClick={insertWhoisMultiple}
                                    disabled={isUploading || selectedFiles.length === 0}
                                    className={`w-full p-2.5 rounded-lg transition-all transform hover:scale-[1.02] ${isUploading || selectedFiles.length === 0
                                        ? 'bg-main-subfont cursor-not-allowed'
                                        : 'bg-main-blue hover:bg-[#5A3DEE] text-white shadow-md'
                                        }`}
                                >
                                    {isUploading ? (
                                        <span className="flex items-center justify-center">
                                            <FaSpinner className="animate-spin mr-2" />
                                            Uploading...
                                        </span>
                                    ) : (
                                        <span className="flex items-center justify-center">
                                            <FaUpload className="mr-2" />
                                            Upload {selectedFiles.length > 0 ? selectedFiles.length : ''} Image{selectedFiles.length !== 1 ? 's' : ''}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Right column: Images gallery */}
                        <div className="lg:col-span-2">
                            {/* Stats Card */}
                            {!isLoading && whoisOptions.length > 0 && (
                                <StatsCard
                                    totalRecords={pageInfo.totalRecords}
                                    selectedOption={selectedOption}
                                    totalPages={pageInfo.totalPages}
                                />
                            )}

                            <div className="bg-main-white rounded-xl shadow-sm p-4 border border-main-border">
                                <div className="flex justify-between items-center mb-4">
                                    <h2 className="text-lg font-semibold text-main-font flex items-center">
                                        <FaImage className="mr-2 text-main-blue" />
                                        Gallery
                                    </h2>

                                    {whoisOptions.length > 0 && (
                                        <div className="flex items-center">
                                            <button
                                                onClick={toggleMultiSelectMode}
                                                className={`flex items-center px-3 py-1.5 rounded-full text-sm mr-2 transition-colors shadow-sm ${isMultiSelectMode
                                                    ? 'bg-main-blue text-white'
                                                    : 'bg-[#F2F0FF] hover:bg-[#E9E5FF] text-main-font'
                                                    }`}
                                            >
                                                {isMultiSelectMode ? (
                                                    <>
                                                        <FaTimes className="mr-1.5" size={12} />
                                                        Cancel
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaCheckSquare className="mr-1.5" size={12} />
                                                        Select
                                                    </>
                                                )}
                                            </button>

                                            {isMultiSelectMode && (
                                                <button
                                                    onClick={toggleSelectAll}
                                                    className="px-3 py-1.5 rounded-full text-sm bg-[#F2F0FF] hover:bg-[#E9E5FF] text-main-font shadow-sm"
                                                >
                                                    {allSelected ? 'Deselect All' : 'Select All'}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {isLoading ? (
                                    <Loader loader={isLoading} />
                                ) : whoisOptions.length === 0 ? (
                                    <div className="text-center py-12 bg-main-bg rounded-lg">
                                        <div className="w-16 h-16 mx-auto mb-4 bg-[#F2F0FF] rounded-full flex items-center justify-center">
                                            <FaImage size={24} className="text-main-blue" />
                                        </div>
                                        <p className="text-main-font">No images found for Option {selectedOption}</p>
                                        <p className="text-sm text-main-subfont mt-1">Upload some images to get started</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                                        {whoisOptions.map(item => (
                                            <ImageCard
                                                key={item._id}
                                                item={item}
                                                isMultiSelectMode={isMultiSelectMode}
                                                isSelected={selectedItems.includes(item._id)}
                                                onToggleSelect={() => toggleItemSelection(item._id)}
                                                onDelete={() => deleteWhois([item._id])}
                                            />
                                        ))}
                                    </div>
                                )}

                                {/* Pagination */}
                                {pageInfo.totalPages > 1 && (
                                    <div className="flex justify-center mt-6">
                                        <div className="inline-flex rounded-full shadow-sm bg-white border border-main-border overflow-hidden">
                                            <button
                                                onClick={() => fetchImageData(selectedOption, 1)}
                                                disabled={pageInfo.currentPage === 1}
                                                className="px-3 py-1.5 disabled:opacity-50 text-main-font hover:bg-[#F2F0FF] flex items-center text-sm"
                                            >
                                                First
                                            </button>
                                            <button
                                                onClick={() => fetchImageData(selectedOption, pageInfo.currentPage - 1)}
                                                disabled={pageInfo.currentPage === 1}
                                                className="px-3 py-1.5 disabled:opacity-50 text-main-font hover:bg-[#F2F0FF] border-l border-r border-main-border"
                                            >
                                                <FaAngleLeft size={14} />
                                            </button>
                                            <div className="px-4 py-1.5 bg-[#F2F0FF] font-medium text-main-font border-r border-main-border">
                                                {pageInfo.currentPage} / {pageInfo.totalPages}
                                            </div>
                                            <button
                                                onClick={() => fetchImageData(selectedOption, pageInfo.currentPage + 1)}
                                                disabled={pageInfo.currentPage === pageInfo.totalPages}
                                                className="px-3 py-1.5 disabled:opacity-50 text-main-font hover:bg-[#F2F0FF] border-r border-main-border"
                                            >
                                                <FaAngleRight size={14} />
                                            </button>
                                            <button
                                                onClick={() => fetchImageData(selectedOption, pageInfo.totalPages)}
                                                disabled={pageInfo.currentPage === pageInfo.totalPages}
                                                className="px-3 py-1.5 disabled:opacity-50 text-main-font hover:bg-[#F2F0FF] flex items-center text-sm"
                                            >
                                                Last
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Controls Tab */}
                {activeTab === 'controls' && (
                    <div className="p-6">
                        {controlsLoading ? (
                            <div className="flex justify-center items-center h-64">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-main-blue"></div>
                            </div>
                        ) : (
                            <>

                                {error && (
                                    <div className="mb-4 p-3 bg-red-50 text-main-delete rounded-md border border-red-200">
                                        {error}
                                    </div>
                                )}

                                {success && (
                                    <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-md border border-green-200">
                                        {success}
                                    </div>
                                )}

                                <div className="bg-main-bg rounded-xl p-5 border border-main-border">
                                    <h2 className="text-lg font-semibold mb-4 text-main-font">Configure Settings</h2>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                        <div>
                                            <label className="block text-main-font mb-2" htmlFor="coin">
                                                <div className="flex items-center">
                                                    <FaCoins className="mr-2 text-main-blue" /> Coins
                                                </div>
                                            </label>
                                            <input
                                                type="number"
                                                id="coin"
                                                name="coin"
                                                value={controlsData.coin}
                                                onChange={handleControlsChange}
                                                className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                                min="1"
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            <p className="text-sm text-main-subfont mt-1">Coins awarded per correct selection</p>
                                        </div>

                                        <div>
                                            <label className="block text-main-font mb-2" htmlFor="limit">
                                                <div className="flex items-center">
                                                    <FaStopwatch className="mr-2 text-main-blue" />Limit
                                                </div>
                                            </label>
                                            <input
                                                type="number"
                                                id="limit"
                                                name="limit"
                                                value={controlsData.limit}
                                                onChange={handleControlsChange}
                                                className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                                min="1"
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            <p className="text-sm text-main-subfont mt-1">Maximum taps allowed per session</p>

                                        </div>

                                        <div>
                                            <label className="block text-main-font mb-2" htmlFor="timing">
                                                <div className="flex items-center">
                                                    <FaClock className="mr-2 text-main-blue" /> Timing (seconds)
                                                </div>
                                            </label>
                                            <input
                                                type="number"
                                                id="timing"
                                                name="timing"
                                                value={controlsData.timing}
                                                onChange={handleControlsChange}
                                                className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                                min="1"
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            <p className="text-sm text-main-subfont mt-1">Time allowed for selection (seconds)</p>
                                        </div>

                                        <div>
                                            <label className="block text-main-font mb-2" htmlFor="timeout_timing">
                                                <div className="flex items-center">
                                                    <FaStopwatch className="mr-2 text-main-blue" /> Timeout (seconds)
                                                </div>
                                            </label>
                                            <input
                                                type="number"
                                                id="timeout_timing"
                                                name="timeout_timing"
                                                value={controlsData.timeout_timing}
                                                onChange={handleControlsChange}
                                                className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                                min="1"
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            <p className="text-sm text-main-subfont mt-1">Timeout between attempts (seconds)</p>
                                        </div>

                                        <div>
                                            <label className="block text-main-font mb-2" htmlFor="ad_position">
                                                <div className="flex items-center">
                                                    <FaAd className="mr-2 text-main-blue" /> Ad Position
                                                </div>
                                            </label>
                                            <input
                                                type="number"
                                                id="ad_position"
                                                name="ad_position"
                                                value={controlsData.ad_position}
                                                onChange={handleControlsChange}
                                                className="w-full p-2 border border-main-border rounded-md focus:outline-none focus:ring-2 focus:ring-main-blue"
                                                min="1"
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            <p className="text-sm text-main-subfont mt-1">Position to show (Fullscreen / Open) ads</p>
                                        </div>
                                    </div>

                                    <div className="flex justify-end">
                                        <button
                                            onClick={saveControls}
                                            disabled={saving}
                                            className={`px-6 py-2.5 rounded-lg text-white ${saving ? 'bg-opacity-70' : 'hover:bg-[#5A3DEE] transform hover:scale-[1.02]'
                                                } bg-main-blue transition-all shadow-md focus:outline-none focus:ring-2 focus:ring-main-blue flex items-center`}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className="mr-2 inline-block h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                                    Saving...
                                                </>
                                            ) : (
                                                'Save Changes'
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
}